import { useSearchParams } from 'react-router-dom';
import RuntimeConfigTab from '../components/config/RuntimeConfigTab';
import GarageOrderingTab from '../components/config/GarageOrderingTab';

/**
 * Admin → Configuration (Spec 061). A tab shell in the FinancePage pattern: the tab lives in
 * `?tab=` so links deep-link and back works. Runtime Config is the default tab; Garage ordering
 * pins up to 20 garages above the customer app's Default ranking.
 */
const TABS: { key: string; label: string }[] = [
  { key: 'runtime-config', label: 'Runtime Config' },
  { key: 'garage-ordering', label: 'Garage ordering' },
];

const DEFAULT_TAB = 'runtime-config';

export default function DynamicConfigPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get('tab');
  const active = TABS.find((t) => t.key === requested)?.key ?? DEFAULT_TAB;

  const selectTab = (key: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', key);
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <h1 className="admin-page-title">Configuration</h1>
        <p className="admin-page-subtitle">Runtime settings and how garages are ordered in the customer app.</p>
      </div>

      {/* Same tab treatment as FinancePage: navy text, yellow underline. */}
      <div
        className="admin-tabs"
        role="tablist"
        style={{ display: 'flex', gap: '0.25rem', marginBottom: '1rem', borderBottom: '1px solid var(--admin-border, #e5e7eb)' }}
      >
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            id={`config-tab-${t.key}`}
            aria-selected={active === t.key}
            aria-controls="config-tabpanel"
            onClick={() => selectTab(t.key)}
            style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: '0.625rem 1rem',
              fontFamily: 'inherit', fontSize: '0.875rem',
              fontWeight: active === t.key ? 600 : 500,
              color: active === t.key ? '#030c54' : 'var(--admin-text-muted, #6b7280)',
              borderBottom: active === t.key ? '2px solid #fff200' : '2px solid transparent',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div role="tabpanel" id="config-tabpanel" aria-labelledby={`config-tab-${active}`}>
        {active === 'runtime-config' && <RuntimeConfigTab />}
        {active === 'garage-ordering' && <GarageOrderingTab />}
      </div>
    </div>
  );
}
